import { RouterProvider, createBrowserRouter, Navigate } from "react-router-dom";
import { Suspense, lazy, useMemo } from "react";
import { useAuth } from "../provider/authProvider";
import { ProtectedRoute } from "./ProtectedRoute";
import Layout from "../Components/Layout";
import { hrRoutes } from "./HrRoutes";
import { adminRoutes } from "./AdminRoutes";
import { businessRoutes } from "./BusinessRoutes";
import { customerCareRoutes } from "./CustomerCareRoutes";
import { mentorRoutes } from "./MentorRoutes";
import { ownerRoutes } from "./OwnerRoutes";
import { accountRoutes } from "./AccountRoutes";
import { developerRoutes } from "./DeveloperRoutes";
import { orgAdminRoutes } from "./OrgAdmin";
import { programCoordinatorRoutes } from "./ProgramCoordinatorRoutes";
import { GlobalLoading } from "../Utils";

// Lazy load page components
const Home = lazy(() => import("../Components/Home/Home"));
const Login = lazy(() => import("../Components/Authentications/Login"));
const Signup = lazy(() => import("../Components/Authentications/Signup"));
const PageNotFound = lazy(() => import("../Pages/PageNotFound"));
const Business = lazy(() => import("../Components/ProfilesType/Business/Business"));
const CustomerCare = lazy(() => import("../Components/ProfilesType/CustomerCare/CustomerCare"));
const Mentor = lazy(() => import("../Components/ProfilesType/Mentor/Mentor"));
const Owner = lazy(() => import("../Components/ProfilesType/Owner/Owner"));
const HR = lazy(() => import("../Components/ProfilesType/HR/HR"));
const Accountant = lazy(() => import("../Components/ProfilesType/Accountants/Accountant"));
const Developer = lazy(() => import("../Components/ProfilesType/Developers/Developer"));
const Admin = lazy(() => import("../Components/ProfilesType/Admin/Admin"));
const Profile = lazy(() => import("../Pages/Profile"));
const ForgotPassword = lazy(() => import("../Components/Authentications/ForgotPassword"));
const ResetPassword = lazy(() => import("../Components/Authentications/ResetPassword"));
const TicketDetails = lazy(() => import("../Components/ProfilesType/Business/Organizations/TicketDetails"));
const OrgAdmin = lazy(() => import("../Components/ProfilesType/OrgAdmin/OrgAdmin"));
const ProgramCoordinator = lazy(() => import("../Components/ProfilesType/ProgramCoordinator/ProgramCoordinator"));
const LtmMeetingRoom = lazy(() => import("../Components/ProfilesType/Business/Organizations/Meetings/MeetingRoom"));

const Routes = () => {
  const { token } = useAuth();
  const routesForPublic = [
    {
      path: "*",
      element: token ? <Suspense fallback={<GlobalLoading />}><PageNotFound /></Suspense> : <Navigate to="/" replace />,
    },
  ];

  const routesForAuthenticatedOnly = [
    {
      path: "/",
      element: <ProtectedRoute />,
      children: [
        {
          path: "/profile",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><Profile /></Suspense></Layout>),
        },
        {
          path: "/home",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><Home /></Suspense></Layout>),
        },
        {
          path: "/",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><Home /></Suspense></Layout>),
        },
        {
          path: "/hr",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><HR /></Suspense></Layout>),
          children: hrRoutes
        },
        {
          path: "/admin",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><Admin /></Suspense></Layout>),
          children: adminRoutes,
        },
        {
          path: "/business",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><Business /></Suspense></Layout>),
          children: businessRoutes,
        },
        {
          path: "/customer-care",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><CustomerCare /></Suspense></Layout>),
          children: customerCareRoutes,
        },
        {
          path: "/mentor",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><Mentor /></Suspense></Layout>),
          children: mentorRoutes,
        },
        {
          path: "/owner",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><Owner /></Suspense></Layout>),
          children: ownerRoutes,
        },
        {
          path: "/accountant",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><Accountant /></Suspense></Layout>),
          children: accountRoutes,
        },
        {
          path: "/developer",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><Developer /></Suspense></Layout>),
          children: developerRoutes,
        },
        {
          path: "/org-admin",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><OrgAdmin /></Suspense></Layout>),
          children: orgAdminRoutes,
        },
        {
          path: "/program-coordinator",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><ProgramCoordinator /></Suspense></Layout>),
          children: programCoordinatorRoutes,
        },
        {
          path: "/ticket/:ticketNumber",
          element: (<Layout><Suspense fallback={<GlobalLoading />}><TicketDetails /></Suspense></Layout>),
        },
        {
          path: "/ltm-room",
          element: (<Suspense fallback={<GlobalLoading />}><LtmMeetingRoom /></Suspense>),
        }
      ],
    },
  ];

  const routesForNotAuthenticatedOnly = [
    {
      path: "/",
      element: (
        <Layout>
          <Suspense fallback={<GlobalLoading />}>
            <Login />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/login",
      element: (
        <Layout>
          <Suspense fallback={<GlobalLoading />}>
            <Login />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/forgot-password",
      element: (
        <Layout>
          <Suspense fallback={<GlobalLoading />}>
            <ForgotPassword />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/signup",
      element: (
        <Layout>
          <Suspense fallback={<GlobalLoading />}>
            <Signup />
          </Suspense>
        </Layout>
      ),
    },
    {
      path: "/reset-password",
      element: (
        <Layout>
          <Suspense fallback={<GlobalLoading />}>
            <ResetPassword />
          </Suspense>
        </Layout>
      ),
    }
  ];

  // Keep guest-only URLs from being treated as unknown routes after login.
  // The authenticated `/` route already renders Home, so it does not need a
  // separate redirect entry here.
  const authenticatedRedirectRoutes = routesForNotAuthenticatedOnly
    .filter(({ path }) => path !== "/")
    .map(({ path }) => ({
      path,
      element: <Navigate to="/" replace />,
    }));

  const router = useMemo(() => createBrowserRouter([
    ...(!token
      ? routesForNotAuthenticatedOnly
      : [...routesForAuthenticatedOnly, ...authenticatedRedirectRoutes]),
    ...routesForPublic,
  ]), [token]);

  return <RouterProvider router={router} />;
};

export default Routes;

// 
